import React, { useMemo } from "react";
import { Table, Tag, Space, Button, Tooltip } from "antd";
import { EyeOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import moment from "moment-jalaali";
import columns from "@staticData/user/myServicesTableColumns";
import {
  SERVICE_TYPES,
  SERVICE_STATUSES,
  getRepurchaseLink,
  isRenewalDisabled,
  getRenewalDisabledReason,
} from "@staticData/services";

function MyServicesTable({
  services,
  totalCount,
  currentPage,
  pageSize,
  loading,
  onPageChange,
  onSort,
  onOpenDetails,
}) {
  const dataSource = useMemo(() => {
    if (!services || !Array.isArray(services)) return [];

    return services.map((item) => {
      const {
        id,
        serviceName,
        serviceType,
        status,
        amount,
        createdAt,
        startAt,
        endAt,
        packageName,
      } = item;

      const statusConfig = SERVICE_STATUSES[status] || {
        label: status,
        color: "default",
      };
      const typeLabel = SERVICE_TYPES[serviceType] || serviceType;
      const isMeetingOrConsultation =
        serviceType === "MEETING_ROOM" || serviceType === "CONSULTATION";

      const formatDate = (date, withTime = false) => {
        if (!date) return "-";
        return withTime
          ? moment(date).format("jYYYY/jMM/jDD | HH:mm")
          : moment(date).format("jYYYY/jMM/jDD");
      };

      const repurchaseUrl = getRepurchaseLink(item);
      const renewalDisabled = isRenewalDisabled(item);
      const disabledReason = getRenewalDisabledReason(item);

      const repurchaseButton = (
        <Button
          type="link"
          size="small"
          icon={<ShoppingCartOutlined />}
          disabled={renewalDisabled}
        >
          تمدید
        </Button>
      );

      return {
        key: id,
        raw: item,
        serviceName: (
          <div>
            <strong>{serviceName}</strong>
            {packageName && (
              <div style={{ fontSize: 12, color: "#888" }}>
                {packageName}
              </div>
            )}
          </div>
        ),
        serviceType: <Tag color="cyan">{typeLabel}</Tag>,
        status: <Tag color={statusConfig.color}>{statusConfig.label}</Tag>,
        startAt: formatDate(startAt, isMeetingOrConsultation),
        endAt: formatDate(endAt, isMeetingOrConsultation),
        amount: amount ? `${amount.toLocaleString()}` : "0",
        createdAt: formatDate(createdAt),
        actions: (
          <Space>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onOpenDetails(item)}
              title="مشاهده جزییات"
            />
            {renewalDisabled ? (
              <Tooltip title={disabledReason}>
                <span>{repurchaseButton}</span>
              </Tooltip>
            ) : (
              <Link to={repurchaseUrl}>
                {repurchaseButton}
              </Link>
            )}
          </Space>
        ),
      };
    });
  }, [services, onOpenDetails]);

  const handleTableChange = (pagination, filters, sorter, extra) => {
    if (extra.action === "sort") {
      onSort(sorter);
    }
    if (extra.action === "paginate") {
      onPageChange(pagination.current);
    }
  };

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      pagination={{
        total: totalCount,
        current: currentPage,
        pageSize: pageSize || 10,
        showSizeChanger: false,
      }}
      onChange={handleTableChange}
      scroll={{ x: 950 }}
      locale={{
        emptyText: "هیچ سرویسی یافت نشد",
      }}
    />
  );
}

export default MyServicesTable;
